import * as _ from 'lodash';
import * as moment from 'moment';
import { DatagridUtilService } from '../services/dx/datagrid-util';

const orderStatus = {
  New: 1,
  Ready: 2,
  Completed: 3,
  'Partially Received': 3,
  Rejected: 4,
};

export const dataCyTags = {
  'item.name': 'item-name-link',
  'item.sku': 'item-sku-link',
  'bin.name': 'bin-link',
  'crib.name': 'crib-link',
  cribName: 'crib-link',
  number: 'PO-number-link',
  'supplier.name': 'supplier-link',
  totalAmount: 'total-amount',
  status: 'status',
  sequence: 'replenishment-number',
  'user-groups.name': 'user-group-name',
  cribRestriction: 'crib-restriction',
  'item.itemAliasSku': 'item-alias-sku',
  'item.itemAliasName': 'item-alias-name',
};

const supplierCellTemplate = (dataCy: string, path: string, value: string) => {
  return `<div class="url ellipsis"><a data-cy="${dataCy}" href="${path}">${value}</a></div>`;
};

// Long text template
export const longTextTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  return value
    ? `<div class="ellipsis">
      ${value}
    </div>`
    : '';
};

export const userIdUrlTemplate = function (row, orgAccess: string[]) {
  const value = _.get(row, this.dataField);
  try {
    const user = _.get(row, 'user');
    const userOrgId = _.get(row, 'user.orgId');
    const path = `#/users/${user?._id}`;
    return value
      ? orgAccess?.includes(userOrgId)
        ? `<div class="url ellipsis">
            ${path ? `<a href="${path}">${value}</a>` : value}
          </div>`
        : `<div class="ellipsis">${value}</div>`
      : '';
  } catch (error) {
    return `<div class="ellipsis">${value}</div>`;
  }
};

export const approvedByIdUrlTemplate = function (row, orgAccess: string[]) {
  const value = _.get(row, this.dataField);
  try {
    const approvedBy = _.get(row, 'approvedBy');
    const approvedByOrgId = _.get(row, 'approvedBy.orgId');
    const path = `#/users/${approvedBy?._id}`;
    return value
      ? orgAccess?.includes(approvedByOrgId)
        ? `<div class="url ellipsis">
            ${path ? `<a href="${path}">${value}</a>` : value}
          </div>`
        : `<div class="ellipsis">${value}</div>`
      : '';
  } catch (error) {
    return `<div class="ellipsis">${value}</div>`;
  }
};

// url template
export const urlTemplate = function (row) {
  const itemId = _.get(row, 'item._id');
  const itemAliasId = _.get(row, 'item.itemAliasId');
  const bin = _.get(row, 'bin');
  const crib = _.get(row, 'crib');
  const supplier = _.get(row, 'supplier');
  const poNumber = _.get(row, 'po');
  const user = _.get(row, 'user');
  const replId = _.get(row, '_id');
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  const paths = {
    'item.name': `#/items/item/${itemId}/overview`,
    'item.sku': `#/items/item/${itemId}/overview`,
    'item.itemAliasSku': `#/items/item/${itemAliasId}/overview`,
    'item.itemAliasName': `#/items/item/${itemAliasId}/overview`,
    sequence: `#/replenishments/${replId}`,
    'crib.name': `#/cribs-and-bins/crib/${crib?._id}`,
    cribName: `#/cribs-and-bins/crib/${row?.cribId}`,
    'bin.name': `#/cribs-and-bins/bin/${bin?._id}`,
    name: `#/cribs-and-bins/bin/${row?._id}`,
    number: `#/purchase-orders/${row?._id}`,
    'po.number': `#/purchase-orders/${poNumber?._id}`,
    'supplier.name': `#/suppliers/${supplier?._id}`,
    'user.fullName': `#/users/${user?._id}`,
    orgName: '#/orgs',
    cribGroupName: `#/manage-groups/crib-group/${row?._id}`,
    'user.profileId': `#/users/${user?._id}`,
  };

  const path = row.path || paths[this.dataField];

  return value
    ? `<div class="url ellipsis">
      ${path ? `<a ${dataCy ? 'data-cy="' + dataCy + '"' : ''}" href="${path}">${value}</a>` : value}
    </div>`
    : '';
};

export const urlAliasNameTemplate = function (row) {
  const itemName = _.get(row, 'item.name');
  const itemSku = _.get(row, 'item.sku');
  const itemAliasId = _.get(row, 'item.itemAliasId');
  const itemAliasName = _.get(row, 'item.itemAliasName');
  const itemAliasSku = _.get(row, 'item.itemAliasSku');
  const itemId = itemAliasId ? itemAliasId : _.get(row, 'item._id');
  const dataCy = _.get(dataCyTags, this.dataField);
  const pathId = itemAliasId ? itemAliasId : itemId;

  const paths = {
    'item.name': `#/items/item/${pathId}/overview`,
    'item.itemAliasName': `#/items/item/${pathId}/overview`,
    'item.sku': `#/items/item/${pathId}/overview`,
    'item.itemAliasSku': `#/items/item/${pathId}/overview`,
  };

  const values = {
    'item.name': itemName,
    'item.itemAliasName': itemAliasName || itemName,
    'item.sku': itemSku,
    'item.itemAliasSku': itemAliasSku || itemSku,
  };

  const path = row.path || paths[this.dataField];

  return values[this.dataField]
    ? `<div class="${itemAliasName ? 'url' : ''} ellipsis">
      ${
        path
          ? `<${itemAliasName ? 'a' : 'span'} ${dataCy ? 'data-cy="' + dataCy + '"' : ''}" href="${path}">${
              values[this.dataField]
            }</${itemAliasName ? 'a' : 'span'}>`
          : values[this.dataField]
      }
    </div>`
    : '';
};

// new tab url template
export const newTabUrlTemplate = function (row) {
  const itemId = _.get(row, 'item._id');
  const bin = _.get(row, 'bin');
  const crib = _.get(row, 'crib');
  const supplier = _.get(row, 'supplier');
  const poNumber = _.get(row, 'po');
  const user = _.get(row, 'user');
  const replId = _.get(row, '_id');
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  const paths = {
    'item.name': `#/items/item/${itemId}/overview`,
    'item.sku': `#/items/item/${itemId}/overview`,
    sequence: `#/replenishments/${replId}`,
    'crib.name': `#/cribs-and-bins/crib/${crib?._id}`,
    cribName: `#/cribs-and-bins/crib/${row?.cribId}`,
    'bin.name': `#/cribs-and-bins/bin/${bin?._id}`,
    name: `#/cribs-and-bins/bin/${row?._id}`,
    number: `#/purchase-orders/${row?._id}`,
    'po.number': `#/purchase-orders/${poNumber?._id}`,
    'supplier.name': `#/suppliers/${supplier?._id}`,
    'user.fullName': `#/users/${user?._id}`,
    orgName: '#/orgs',
  };

  const path = paths[this.dataField];

  return value
    ? `<div class="url ellipsis">
      ${path ? `<a target="_blank" ${dataCy ? 'data-cy="' + dataCy + '"' : ''}" href="${path}">${value}</a>` : value}
    </div>`
    : '';
};

export const schedulingUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/scheduling/' + row._id;

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

export const costCenterUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/cost-centers/' + (row.costCenterId || row._id);

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

export const customerUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/customers/' + row._id;

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

export const usersUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/users/' + row._id;

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

export const cribsUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/cribs-and-bins/crib/' + row._id;

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

export const issuedToUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/users/' + row.user?._id;

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

export const checkedOutQuantityUrlTemplate = function (row) {
  const value = _.get(row, this.dataField) ?? 0;
  const durable = _.get(row, 'item.itemType') === 'Durable';
  const path = `#/cribs-and-bins/bin/${row?._id}`;

  return durable ? `<div class="url ellipsis"> <a href="${path}">${value}</a></div>` : '';
};

export const itemLimitsUrlTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/restrictions/item-restrictions/' + row?._id;
  return value
    ? `<div class="url ellipsis">
      ${path ? `<a href="${path}">${value}</a>` : value}
      </div>`
    : '';
};

// url divergent template
// (cases for the URLs from "urlTemplate" with different behavior)
export const urlDivergentTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  const paths = {
    sequence: `#/purchase-orders/${row?._id}`,
  };

  const path = paths[this.dataField];

  return value
    ? `<div class="url ellipsis">
      ${path ? `<a ${dataCy ? 'data-cy="' + dataCy + '"' : ''}" href="${path}">${value}</a>` : value}
    </div>`
    : '';
};

// Bins url template
export const urlTemplateBins = function (row) {
  const itemId = _.get(row, 'item')?._id;
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  const paths = {
    'item.sku': `#/items/item/${itemId}/overview`,
    name: `#/cribs-and-bins/bin/${row?._id}`,
    cribName: `#/cribs-and-bins/crib/${row?.cribId}`,
  };

  const path = paths[this.dataField];

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a ${dataCy ? 'data-cy="' + dataCy + '"' : ''}" href="${path}">${value}</a>` : value}
  </div>`
    : '<div class="url ellipsis"></div>';
};

// Dollars url template
export const dollarCurrencyTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  return value
    ? `<div class="ellipsis" ${dataCy ? 'data-cy="' + dataCy + '"' : ''}>
        $${Number(value).toLocaleString('en-GB', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>`
    : '<div class="ellipsis">$0</div>';
};

// Chip url template
export const chipTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const dataCy = _.get(dataCyTags, this.dataField);

  if (!value || value.length < 1) {
    return '';
  }

  return `<div class="chip chip--small chip--outlined chip--default" ${dataCy ? 'data-cy="' + dataCy + '"' : ''}>
            ${value}
          </div>`;
};

export const isRequiredChipsTemplate = function (row) {
  const isRequired = _.get(row, this.dataField);
  if (isRequired === null) {
    return '';
  }
  const configColor = isRequired ? 'danger' : 'default';
  const label = isRequired ? 'Required' : 'Not Required';
  const chip = `
    <div class="chip chip--small chip--filled chip--${configColor}">
      ${label}
    </div>
  `;
  return chip;
};

export const dateTemplateMMDDYY = function (row) {
  const value = _.get(row, this.dataField);
  const formatedDueDate = moment(value).format('MM/DD/YY');
  return `<div data-cy="date-column">
            ${formatedDueDate}
          </div>`;
};

// Date url template
export const dateTemplateMMDDYYYY = function (row) {
  const value = _.get(row, this.dataField);
  let formatedDueDate = '---';
  if (value) {
    formatedDueDate = moment(value).format('MM/DD/YYYY');
  }
  return `<div data-cy="date-column">
              ${formatedDueDate === 'Invalid date' ? '---' : formatedDueDate}
            </div>`;
};

// single column item image template
export const itemTemplate = function (row) {
  const sku = _.get(row, 'item.sku');
  const dataCy = _.get(dataCyTags, this.dataField);
  const path = `#/items?sku=${sku}`;
  const item = `
    <div class="url item-col ellipsis">
        <a ${dataCy ? 'data-cy="' + dataCy + '"' : ''}" href="${path}">${row?.item?.name ?? ''}</a>
    </div>`;
  return item;
};

// patients template
export const patientTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const patientID = _.get(row, '_id');
  const dataCy = _.get(dataCyTags, this.dataField);
  const paths = {
    name: `#/patients/${patientID}`,
    email: `mailto:${value}`,
  };
  const path = paths[this.dataField];

  return patientID && value ? supplierCellTemplate(dataCy, path, value) : '';
};

// suppliers template
export const supplierTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const supplierID = _.get(row, '_id');
  const dataCy = _.get(dataCyTags, this.dataField);
  const paths = {
    name: `#/suppliers/${supplierID}`,
    email: `mailto:${value}`,
  };
  const path = paths[this.dataField];

  return supplierID && value ? supplierCellTemplate(dataCy, path, value) : '';
};

export const fireIncidentTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const path = '#/fire/' + row._id;

  return value
    ? `<div class="url ellipsis">
    ${path ? `<a href="${path}">${value}</a>` : value}
  </div>`
    : '';
};

// item suppliers template
export const itemSuppliersTemplate = function (row) {
  const value = _.get(row, this.dataField);
  const supplierID = _.get(row, 'supplier._id');
  const dataCy = _.get(dataCyTags, this.dataField);
  const path = `#/suppliers/${supplierID}`;

  return supplierID && value ? supplierCellTemplate(dataCy, path, value) : '';
};

export const numberHashtagTemplate = function (row) {
  const value = _.get(row, this.dataField);

  return `<span>#${value}</span>`;
};

export const freeformToTextTemplate = function (row) {
  const bool = _.get(row, this.dataField);
  return bool === null ? null : bool ? 'No' : 'Yes';
};

// generic boolean formatter
export const booleanToTextTemplate = function (row) {
  const bool = _.get(row, this.dataField);
  return bool === false ? 'No' : 'Yes';
};

//validate if row user belongs to logged in user org
export const userBelongsToMyOrgTemplate = function (row, userOrgId) {
  const value = _.get(row, this.dataField);
  const orgId = value ? value : '';
  const path = '#/users/' + row[this.dataField.replace('.profileId', '').trim()]?._id;
  return userOrgId === row.orgId
    ? `<div class="url ellipsis">
          ${`<a href="${path}">${orgId}</a>`}
       </div>`
    : orgId;
};
/**
 * calculateFilterValue: true,
 * customFilterFunction: requiredFilter,
 */
export function inactiveFilter(result: any) {
  return result.map((x) => {
    return {
      ...x,
      text: x.value ? 'No' : 'Yes',
    };
  });
}

export function inactiveTextTemplate(row) {
  const bool = _.get(row, this.dataField);
  return bool ? 'No' : 'Yes';
}

export const badgeTemplate = (list: any[], chipMode: string, dataField, width?: number) => {
  if (!list?.length) {
    return '';
  }

  return DatagridUtilService.gridTagsTemplate({
    tags: list,
    maxWidth: width,
    tagWrapper: `<div class="chip chip--small {{class}} chip--${chipMode} chip--default tags-shown">{{tag}}</div> `,
    paddingWidth: null,
    tagWithLink: true,
    dataField,
  });
};

export const headerFilterArrays = (result) => {
  const allKeys = result?.flatMap((x) => x.key)?.map((x) => x?.key);
  const uniqueKeys = allKeys.filter((value, index) => allKeys.indexOf(value) === index);

  return uniqueKeys.map((x) => {
    return {
      count: 1,
      key: x,
      text: x === null || x === undefined ? '(Blanks)' : x,
      value: x === null || x === undefined ? null : x,
    };
  });
};

/**
 * Generic entity link template for use in data grids.
 * @param {Object} options
 *   - entityPath: string | (row) => string
 *   - field: string (defaults to this.dataField)
 *   - dataCyKey: string (optional, key in dataCyTags)
 */
export function entityLinkTemplateFactory(options: {
  entityPath: string | ((row: any) => string),
  field?: string,
  dataCyKey?: string
}) {
  return function (row: any) {
    const field = options.field || this.dataField;
    const value = _.get(row, field);
    if (!value) return '';
    const path = typeof options.entityPath === 'function' ? options.entityPath(row) : options.entityPath;
    const dataCy = options.dataCyKey ? _.get(dataCyTags, options.dataCyKey) : _.get(dataCyTags, field);
    return `<div class="url ellipsis">
      <a${dataCy ? ' data-cy="' + dataCy + '"' : ''} href="${path}">${value}</a>
    </div>`;
  };
}

export const parseCustomerAddress = (row) => {
  return `${row.address?.street || ''}`;
};
